/**
 * Phone Number Formatter Utility
 * Formats US phone numbers as: XXX-XXX-XXXX
 */

/**
 * Format a phone number string with dashes
 * @param {string} value - Raw phone input
 * @returns {string} - Formatted phone number (e.g., 732-779-9221)
 */
export const formatPhoneNumber = (value) => {
  if (!value) return '';
  let digits = String(value).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1); // tolerate leading US 1
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Get raw digits from formatted phone number
 * @param {string} value - Formatted phone number
 * @returns {string} - Raw digits only
 */
export const getPhoneDigits = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

/**
 * Format a phone number for DISPLAY as XXX-XXX-XXXX (e.g. 401-772-9034).
 * Display-only — the input helper (formatPhoneNumber) uses the same dash style
 * so a number looks identical everywhere it appears.
 * @param {string} value - Raw or formatted phone
 * @returns {string} - Dash display string, or the original if non-standard
 */
export const formatPhoneDisplay = (value) => {
  if (!value) return '';
  let digits = String(value).replace(/\D/g, '');
  // 11-digit leading 1 -> strip the 1, format the rest
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  // US 10-digit -> XXX-XXX-XXXX
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Anything non-standard: return the original value unchanged (graceful)
  return String(value).trim();
};

/**
 * Format a phone number for DASH display/input as XXX-XXX-XXXX (e.g. 401-464-1416).
 * Tolerates a leading country "1". Anything that isn't a clean 10-digit number
 * is returned as entered (trimmed) rather than mangled.
 * @param {string} value - Raw or formatted phone
 * @returns {string} - Dash-formatted string, or the original if non-standard
 */
export const formatPhoneDashes = (value) => {
  if (!value) return '';
  let digits = String(value).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // While typing (fewer than 10 digits) build the dashed form progressively so
  // the input field formats live; non-numeric / overlong values pass through.
  if (digits.length > 0 && digits.length < 10 && String(value).replace(/[\d\s\-()]/g, '') === '') {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return String(value).trim();
};

/**
 * Validate if phone number has 10 digits
 * @param {string} value - Phone number (formatted or raw)
 * @returns {boolean} - True if valid
 */
export const isValidPhoneNumber = (value) => {
  const digits = getPhoneDigits(value);
  return digits.length === 10;
};
